import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import ProductionWorkSheets from '@/components/ProductionWorkSheets';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import {
  DEFAULT_PRODUCTION_PRIORITY,
  DEFAULT_PRODUCTION_STATUS,
  NEXT_PRODUCTION_STATUS,
  PRODUCTION_PRIORITIES,
  PRODUCTION_PRIORITY_LABELS,
  PRODUCTION_STATUSES,
  PRODUCTION_STATUS_LABELS,
} from '@/constants/production';
import '@/pages/Products.css';
import '@/pages/Clients.css';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-AR');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('es-AR')} ${date.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('es-AR');

const ProductionDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isWorker = user?.role === 'trabajador';
  const [order, setOrder] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [patch, setPatch] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activityError, setActivityError] = useState('');

  const loadActivity = useCallback(async () => {
    if (isWorker) {
      setActivity([]);
      setActivityError('');
      return;
    }
    try {
      setActivityError('');
      const { data } = await api.get(`/production-orders/${id}/activity`);
      setActivity(data || []);
    } catch (err) {
      setActivityError('Error al cargar actividad');
      setActivity([]);
    }
  }, [id, isWorker]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const requests = [api.get(`/production-orders/${id}`)];
      if (!isWorker) requests.push(api.get('/workers'));
      const [orderRes, workersRes] = await Promise.all(requests);
      const loadedOrder = orderRes.data;
      if (loadedOrder?.id && loadedOrder.id !== id) {
        navigate(`/produccion/${loadedOrder.id}`, { replace: true });
      }
      setOrder(loadedOrder);
      setPatch({
        assigned_worker_id: loadedOrder.assigned_worker_id || '',
        delivery_date: loadedOrder.delivery_date || '',
        production_notes: loadedOrder.production_notes || '',
        priority: loadedOrder.priority || DEFAULT_PRODUCTION_PRIORITY,
      });
      if (workersRes) setWorkers(workersRes.data || []);
      await loadActivity();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al cargar orden de produccion');
    } finally {
      setLoading(false);
    }
  }, [id, isWorker, loadActivity, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateOrder = (updated) => {
    setOrder(updated);
    setPatch({
      assigned_worker_id: updated.assigned_worker_id || '',
      delivery_date: updated.delivery_date || '',
      production_notes: updated.production_notes || '',
      priority: updated.priority || DEFAULT_PRODUCTION_PRIORITY,
    });
  };

  const saveAdminChanges = async () => {
    if (!order) return;
    if (!patch.assigned_worker_id) {
      alert('Selecciona un trabajador para esta orden');
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.put(`/production-orders/${order.id}`, patch);
      updateOrder(data);
      await loadActivity();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al guardar orden');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    if (!order || !status) return;
    try {
      setSaving(true);
      const { data } = await api.put(`/production-orders/${order.id}/status`, { status });
      updateOrder(data);
      await loadActivity();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al cambiar estado');
    } finally {
      setSaving(false);
    }
  };

  const status = order?.status || DEFAULT_PRODUCTION_STATUS;
  const priority = order?.priority || DEFAULT_PRODUCTION_PRIORITY;
  const nextStatus = NEXT_PRODUCTION_STATUS[status];
  const image = order?.design_file_url || order?.product?.image_url;
  const orderNumber = order ? String(order.order_number || order.production_number || '').padStart(4, '0') : '';
  const budgetNumber = order ? String(order.budget_number || '').padStart(4, '0') : '';

  const printInternalOrder = useCallback(() => {
    window.print();
  }, []);

  const headerActions = useMemo(() => (
    <div className="page-actions">
      <button className="btn-secondary" onClick={() => navigate('/produccion')}>
        Volver
      </button>
      {!isWorker && order?.budget_id && (
        <Link className="btn-secondary" to={`/presupuestos/${order.budget_id}`}>
          Ver presupuesto
        </Link>
      )}
      {order && (
        <button className="btn-secondary" onClick={printInternalOrder}>
          Imprimir orden interna
        </button>
      )}
      <button className="btn-secondary" onClick={load} disabled={loading}>
        Actualizar
      </button>
    </div>
  ), [isWorker, load, loading, navigate, order, printInternalOrder]);

  return (
    <div className="dashboard-container">
      <Sidebar activePath="/produccion" />

      <main className="main-content production-detail-page">
        <div className="page-header-row">
          <div>
            <h1>{order ? `Orden #${orderNumber}` : 'Orden de produccion'}</h1>
            <p className="dashboard-subtitle">
              {order ? `Presupuesto #${budgetNumber} - ${PRODUCTION_STATUS_LABELS[status] || status}` : 'Detalle completo de produccion'}
            </p>
          </div>
          {headerActions}
        </div>

        {error && <div className="login-error">{error}</div>}

        {loading ? (
          <div className="empty-state">Cargando orden...</div>
        ) : order ? (
          <>
            <section className="card production-detail-summary">
              <div className="production-detail-head">
                <div>
                  <h2>{order.product?.name || '-'}</h2>
                  <p>Cliente: <strong>{order.client?.name || '-'}</strong></p>
                </div>
                <div className="production-card-badges">
                  <span className={`production-priority production-priority-${priority}`}>
                    {PRODUCTION_PRIORITY_LABELS[priority] || priority}
                  </span>
                  <span className={`production-status production-status-${status}`}>
                    {PRODUCTION_STATUS_LABELS[status] || status}
                  </span>
                </div>
              </div>

              <div className="production-detail-grid">
                <div className="production-mini-image large">
                  {image ? <img src={image} alt={order.product?.name || 'Diseno'} /> : <span>Sin diseno</span>}
                </div>
                <div className="production-info">
                  <div><strong>Cantidad:</strong> <span className="mono">{formatNumber(order.quantity)}</span></div>
                  <div><strong>Entrega:</strong> {formatDate(order.delivery_date)}</div>
                  <div><strong>Trabajador:</strong> {order.assigned_worker_name || '-'}</div>
                  <div><strong>Creada:</strong> {formatDateTime(order.created_at)}</div>
                  {order.finished_at && <div><strong>Finalizada:</strong> {formatDateTime(order.finished_at)}</div>}
                  {order.delivered_at && <div><strong>Entregada:</strong> {formatDateTime(order.delivered_at)}</div>}
                </div>
              </div>
            </section>

            <section className="card">
              <h2>Gestion de orden</h2>
              <hr />
              {isWorker ? (
                <div className="production-detail-controls worker">
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-input"
                      value={status}
                      onChange={(e) => changeStatus(e.target.value)}
                      disabled={saving}
                    >
                      {PRODUCTION_STATUSES.map(item => (
                        <option key={item.key} value={item.key}>{item.label}</option>
                      ))}
                    </select>
                  </div>
                  {nextStatus && (
                    <button className="btn-primary-inline" onClick={() => changeStatus(nextStatus)} disabled={saving}>
                      Pasar a {PRODUCTION_STATUS_LABELS[nextStatus]}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="production-detail-controls">
                    <div className="form-group">
                      <label className="form-label">Trabajador</label>
                      <select
                        className="form-input"
                        value={patch.assigned_worker_id || ''}
                        onChange={(e) => setPatch(current => ({ ...current, assigned_worker_id: e.target.value }))}
                      >
                        <option value="">Seleccionar trabajador</option>
                        {workers.map(worker => (
                          <option key={worker.id} value={worker.id}>{worker.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Estado</label>
                      <select
                        className="form-input"
                        value={status}
                        onChange={(e) => changeStatus(e.target.value)}
                        disabled={saving}
                      >
                        {PRODUCTION_STATUSES.map(item => (
                          <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Prioridad</label>
                      <select
                        className="form-input"
                        value={patch.priority || DEFAULT_PRODUCTION_PRIORITY}
                        onChange={(e) => setPatch(current => ({ ...current, priority: e.target.value }))}
                      >
                        {PRODUCTION_PRIORITIES.map(item => (
                          <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Entrega</label>
                      <input
                        className="form-input"
                        type="date"
                        value={patch.delivery_date || ''}
                        onChange={(e) => setPatch(current => ({ ...current, delivery_date: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Observaciones de produccion</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={patch.production_notes || ''}
                      onChange={(e) => setPatch(current => ({ ...current, production_notes: e.target.value }))}
                    />
                  </div>
                  <button className="btn-primary" onClick={saveAdminChanges} disabled={saving}>
                    Guardar cambios
                  </button>
                </>
              )}
            </section>

            <section className="card">
              <h2>Observaciones de produccion</h2>
              <hr />
              <p>{order.production_notes || '-'}</p>
            </section>

            <div className="production-print-area">
              <div className="production-print-heading">
                <div>
                  <h1>Kontinuos S.R.L.</h1>
                  <p>{formatDateTime(new Date())}</p>
                </div>
                <div>
                  <h2>Orden interna #{orderNumber}</h2>
                  <p>Presupuesto #{budgetNumber}</p>
                </div>
                <div className="production-print-summary">
                  <p><strong>Cliente:</strong> {order.client?.name || '-'}</p>
                  <p><strong>Producto:</strong> {order.product?.name || '-'}</p>
                  <p><strong>Cantidad:</strong> {formatNumber(order.quantity)}</p>
                  <p><strong>Entrega:</strong> {formatDate(order.delivery_date)}</p>
                </div>
              </div>

              <section className="card production-print-card">
                <h2>Orden interna e imposicion</h2>
                <hr />
                <ProductionWorkSheets order={order} large />
              </section>
            </div>

            {!isWorker && (
              <section className="card">
                <h2>Actividad</h2>
                <hr />
                {activityError && <div className="login-error">{activityError}</div>}
                {activity.length === 0 ? (
                  <div className="empty-state compact-empty">Todavia no hay actividad registrada.</div>
                ) : (
                  <div className="activity-timeline">
                    {activity.map(entry => (
                      <Link
                        key={entry.id}
                        className="activity-item"
                        to={entry.link || location.pathname}
                      >
                        <span className="activity-dot"></span>
                        <div>
                          <div className="activity-meta">
                            {formatDateTime(entry.created_at)} - {entry.actor_name || '-'} - {entry.actor_role || '-'}
                          </div>
                          <p>{entry.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        ) : (
          <div className="empty-state">Orden no encontrada.</div>
        )}
      </main>
    </div>
  );
};

export default ProductionDetail;
